import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Fallback customizado (opcional) */
  fallback?: ReactNode;
  /** Nível visual: 'page' (tela cheia), 'panel' (box compacto), 'widget' (card inline) */
  nivel?: 'page' | 'panel' | 'widget';
  /** Callback opcional para logging externo */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /**
   * Sprint A — Reset automático do boundary quando qualquer valor mudar.
   * Útil para resetar erro ao navegar entre PEAs/abas sem reload.
   */
  resetKeys?: unknown[];
  /**
   * Sprint A — Callback chamado ao clicar em "Tentar novamente".
   * Use para invalidar queries específicas via `queryClient.invalidateQueries({ queryKey })`.
   */
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

const arraysIguais = (a?: unknown[], b?: unknown[]) => {
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  return a.every((v, i) => Object.is(v, b[i]));
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (
      this.state.hasError &&
      !arraysIguais(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.setState({ hasError: false, error: null });
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const nivel = this.props.nivel ?? 'panel';

    if (nivel === 'widget') {
      return (
        <div className="flex flex-col items-center justify-center gap-2 p-4 text-center rounded-lg border border-destructive/20 bg-destructive/5">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <p className="text-xs text-muted-foreground">Falha ao carregar</p>
          <Button variant="ghost" size="sm" onClick={this.handleRetry} className="h-7 text-xs gap-1">
            <RefreshCw className="h-3 w-3" /> Tentar novamente
          </Button>
        </div>
      );
    }

    if (nivel === 'panel') {
      return (
        <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
          <AlertTriangle className="h-8 w-8 text-destructive" />
          <div>
            <p className="text-sm font-medium text-foreground">Algo deu errado</p>
            <p className="text-xs text-muted-foreground mt-1">
              {this.state.error?.message || 'Ocorreu um erro inesperado.'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={this.handleRetry} className="gap-2">
            <RefreshCw className="h-4 w-4" /> Recarregar painel
          </Button>
        </div>
      );
    }

    // nivel === 'page'
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <div>
          <h2 className="text-lg font-semibold text-foreground">Erro inesperado</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-md">
            {this.state.error?.message || 'Ocorreu um erro ao carregar esta página. Tente novamente.'}
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={this.handleRetry} className="gap-2">
            <RefreshCw className="h-4 w-4" /> Tentar novamente
          </Button>
          <Button variant="default" onClick={() => window.location.reload()}>
            Recarregar página
          </Button>
        </div>
      </div>
    );
  }
}
